import {
  Alert,
  Form,
  Input,
  Modal,
  Radio,
  RadioChangeEvent,
  Select,
} from 'antd';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { OrganizationType } from 'protocol/model/dto_organization_pb';

import { i18n, LANGUAGE_OPTIONS, PATHS } from '@/shared/config';
import { normalizeEmail } from '@/shared/lib';

import {
  DSOS_FILTER,
  OrganizationSearchSelect,
  organizationsMessages,
  useOrganizationsByOwnerEmail,
} from '@/entities/organizations';

import { useCountries } from '@/features/countries';

import styles from './CreateAccountModal.module.scss';

export type CreateAccountVariant = 'signUp' | 'inviteOwner';

export type CreateAccountSubmitPayload =
  | { variant: 'signUp' }
  | {
      variant: 'inviteOwner';
      organizationName: string;
      organizationType: OrganizationType;
      ownerEmail: string;
      ownerFirstName: string;
      ownerLastName: string;
      ownerLanguage: string;
      ownerCountry: string;
      dsoParentID?: string;
    };

type CreateAccountFormFields = {
  organizationName?: string;
  organizationType: OrganizationType;
  ownerEmail?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  ownerLanguage?: string;
  ownerCountry?: string;
  dsoParentID?: string;
};

type CreateAccountModalProps = {
  open: boolean;
  canManageDSO: boolean;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateAccountSubmitPayload) => void;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CreateAccountModal: FC<CreateAccountModalProps> = (props) => {
  const { open, canManageDSO, loading, onCancel, onSubmit } = props;

  const { formatMessage } = useIntl();

  const [variant, setVariant] = useState<CreateAccountVariant>('signUp');
  const [form] = Form.useForm<CreateAccountFormFields>();
  const countryOptions = useCountries();

  const organizationName = Form.useWatch('organizationName', form);
  const ownerEmail = Form.useWatch('ownerEmail', form);
  const ownerFirstName = Form.useWatch('ownerFirstName', form);
  const ownerLastName = Form.useWatch('ownerLastName', form);
  const ownerLanguage = Form.useWatch('ownerLanguage', form);
  const ownerCountry = Form.useWatch('ownerCountry', form);

  const isOwnerEmailValid =
    Boolean(ownerEmail?.trim()) &&
    emailRegex.test(normalizeEmail(ownerEmail ?? ''));

  // An organization is created for its owner, so an email that already owns one
  // cannot be invited again — the lookup gates the submit.
  const {
    checking: isCheckingOwnerEmail,
    organizations: organizationsWithSameOwner,
  } = useOrganizationsByOwnerEmail({
    email: ownerEmail ?? '',
    enabled: open && variant === 'inviteOwner' && isOwnerEmailValid,
  });

  const hasOrganizationWithSameOwner = organizationsWithSameOwner.length > 0;

  useEffect(() => {
    if (!open) {
      setVariant('signUp');
      form.resetFields();
    }
  }, [open, form]);

  const handleVariantChange = (event: RadioChangeEvent) => {
    setVariant(event.target.value as CreateAccountVariant);
  };

  const handleOk = async () => {
    if (variant === 'signUp') {
      onSubmit({ variant: 'signUp' });

      return;
    }

    if (hasOrganizationWithSameOwner) {
      return;
    }

    const values = await form.validateFields();

    onSubmit({
      variant: 'inviteOwner',
      organizationName: values.organizationName!.trim(),
      organizationType: values.organizationType,
      ownerEmail: normalizeEmail(values.ownerEmail!),
      ownerFirstName: values.ownerFirstName!.trim(),
      ownerLastName: values.ownerLastName!.trim(),
      ownerLanguage: values.ownerLanguage!,
      ownerCountry: values.ownerCountry!,
      dsoParentID: values.dsoParentID,
    });
  };

  const isInviteOwnerValid =
    Boolean(organizationName?.trim()) &&
    isOwnerEmailValid &&
    Boolean(ownerFirstName?.trim()) &&
    Boolean(ownerLastName?.trim()) &&
    Boolean(ownerLanguage) &&
    Boolean(ownerCountry);

  const isOkDisabled =
    variant === 'inviteOwner' &&
    (!isInviteOwnerValid ||
      isCheckingOwnerEmail ||
      hasOrganizationWithSameOwner);

  return (
    <Modal
      title={formatMessage({
        id: 'users.createAccount',
        defaultMessage: 'Create Account',
      })}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={formatMessage({
        id: 'global.create',
        defaultMessage: 'Create',
      })}
      okButtonProps={{ disabled: isOkDisabled }}
      confirmLoading={loading}
    >
      <Radio.Group
        className={styles.radioGroup}
        value={variant}
        onChange={handleVariantChange}
      >
        <Radio value="signUp">
          {formatMessage({
            id: 'createAccountModal.signUpOption',
            defaultMessage: 'Create account via sign-up',
          })}
        </Radio>

        <Radio value="inviteOwner">
          {formatMessage({
            id: 'createAccountModal.inviteOwnerOption',
            defaultMessage:
              'Create organization for an unregistered user and send invitation to owner',
          })}
        </Radio>
      </Radio.Group>

      {variant === 'inviteOwner' && (
        <Form
          name="createAccountForm"
          form={form}
          layout="vertical"
          className={styles.form}
          initialValues={{
            organizationType: OrganizationType.OrganizationType_Dental,
          }}
        >
          <Form.Item
            name="ownerEmail"
            label={formatMessage({
              id: 'createAccountModal.ownerEmail.label',
              defaultMessage: 'Owner email',
            })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'forms.required',
                  defaultMessage: 'Field is required',
                }),
              },
              {
                type: 'email',
                message: formatMessage({
                  id: 'yup.email',
                  defaultMessage: 'Incorrect email',
                }),
              },
            ]}
          >
            <Input
              type="email"
              placeholder={formatMessage({
                id: 'createAccountModal.ownerEmail.placeholder',
                defaultMessage: 'Enter owner email',
              })}
            />
          </Form.Item>

          {hasOrganizationWithSameOwner && (
            <Alert
              className={styles.existingOrganizationAlert}
              type="error"
              showIcon
              message={formatMessage({
                id: 'createAccountModal.ownerEmail.organizationExists',
                defaultMessage:
                  'An organization with this owner email already exists',
              })}
              description={
                <div className={styles.existingOrganizationLinks}>
                  {organizationsWithSameOwner.map((organization) => (
                    <Link
                      key={organization.ID}
                      to={generatePath(PATHS.organization, {
                        organizationID: organization.ID,
                      })}
                      target="_blank"
                    >
                      {organization.Name || organization.OwnerEmail}
                    </Link>
                  ))}
                </div>
              }
            />
          )}

          <Form.Item
            name="organizationName"
            label={formatMessage({
              id: 'createAccountModal.organizationName.label',
              defaultMessage: 'Organization name',
            })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'forms.required',
                  defaultMessage: 'Field is required',
                }),
              },
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: 'createAccountModal.organizationName.placeholder',
                defaultMessage: 'Enter organization name',
              })}
            />
          </Form.Item>

          <Form.Item
            name="organizationType"
            label={formatMessage({
              id: 'createAccountModal.organizationType.label',
              defaultMessage: 'Organization type',
            })}
          >
            <Radio.Group>
              <Radio value={OrganizationType.OrganizationType_Dental}>
                {formatMessage(
                  i18n.organizationTypeDefaultMessages[
                    OrganizationType.OrganizationType_Dental
                  ],
                )}
              </Radio>
              <Radio value={OrganizationType.OrganizationType_NonDental}>
                {formatMessage(
                  i18n.organizationTypeDefaultMessages[
                    OrganizationType.OrganizationType_NonDental
                  ],
                )}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="ownerFirstName"
            label={formatMessage({
              id: 'createAccountModal.ownerFirstName.label',
              defaultMessage: 'Owner first name',
            })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'forms.required',
                  defaultMessage: 'Field is required',
                }),
              },
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: 'createAccountModal.ownerFirstName.placeholder',
                defaultMessage: 'Enter owner first name',
              })}
            />
          </Form.Item>

          <Form.Item
            name="ownerLastName"
            label={formatMessage({
              id: 'createAccountModal.ownerLastName.label',
              defaultMessage: 'Owner last name',
            })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'forms.required',
                  defaultMessage: 'Field is required',
                }),
              },
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: 'createAccountModal.ownerLastName.placeholder',
                defaultMessage: 'Enter owner last name',
              })}
            />
          </Form.Item>

          <Form.Item
            name="ownerLanguage"
            label={formatMessage({
              id: 'createAccountModal.ownerLanguage.label',
              defaultMessage: 'Owner language',
            })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'forms.required',
                  defaultMessage: 'Field is required',
                }),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="searchLabel"
              options={LANGUAGE_OPTIONS}
              placeholder={formatMessage({
                id: 'createAccountModal.ownerLanguage.placeholder',
                defaultMessage: 'Select owner language',
              })}
            />
          </Form.Item>

          <Form.Item
            name="ownerCountry"
            label={formatMessage({
              id: 'createAccountModal.ownerCountry.label',
              defaultMessage: 'Owner country',
            })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'forms.required',
                  defaultMessage: 'Field is required',
                }),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={countryOptions}
              placeholder={formatMessage({
                id: 'createAccountModal.ownerCountry.placeholder',
                defaultMessage: 'Select owner country',
              })}
            />
          </Form.Item>

          <Form.Item
            name="dsoParentID"
            label={formatMessage(organizationsMessages.parentDSOLabel)}
          >
            <OrganizationSearchSelect
              disabled={!canManageDSO}
              filter={DSOS_FILTER}
              placeholder={formatMessage(
                organizationsMessages.searchPlaceholder,
              )}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
